// DevFloorPlan — the developer's own whole-floor plan under a developer card, with the card's flats highlighted.
//
// Owner 2026-09-21: "Can you also get floor plans" (Simetria screenshot: floor 4, flats #401–#412). Data: core 1725
// development_floor_plan (one image per building+floor) + development_unit.polygon, via
// GET /api/core/developments/floorplan. Outlines arrive in the plan's own coordinate space (coord_w × coord_h: % of the
// image for Solum, SVG viewBox units for Simetria) and are scaled into the drawing space below (see `space`).
// Loaded on click only: a feed of 150 cards must not fetch 150 floor images.
import { useState } from 'react'
import { t } from '../../lib/i18n.js'
import { getDevelopmentFloorplan } from '../../db/core.js'

const FREE = new Set(['available', 'reserved'])
// "x,y, x,y, …" in the plan's coord space → SVG points scaled by (sx, sy) into the drawing space
const pts = (poly, sx = 1, sy = 1) => String(poly || '').split(',').map(v => +v.trim()).reduce((acc, v, i, a) =>
  (i % 2 ? acc : [...acc, `${+(v * sx).toFixed(2)},${+(a[i + 1] * sy).toFixed(2)}`]), []).join(' ')
// Drawing space: the image's own pixels when its size is known (Solum's outlines are % of the image — drawn in a
// 100 × 100 box the image would be stretched square), else the coord space itself (Simetria: the SVG viewBox the
// developer drew on, which already has the image's proportions).
const space = p => (p.image_w && p.image_h ? { w: p.image_w, h: p.image_h, sx: p.image_w / p.coord_w, sy: p.image_h / p.coord_h }
  : { w: p.coord_w, h: p.coord_h, sx: 1, sy: 1 })

export default function DevFloorPlan({ propertyId, className = '' }) {
  const [plans, setPlans] = useState(null)       // null = not loaded, [] = none published
  const [open, setOpen] = useState(false)
  const [idx, setIdx] = useState(0)
  const [err, setErr] = useState(null)
  if (!propertyId) return null

  const toggle = async e => {
    e.stopPropagation()
    if (!open && plans == null) {
      try { setPlans(await getDevelopmentFloorplan(propertyId)) } catch (x) { setErr(x.message); setPlans([]) }
    }
    setOpen(o => !o)
  }
  const plan = plans?.[Math.min(idx, (plans?.length || 1) - 1)]
  const sp = plan ? space(plan) : null

  return (
    <div className={className} onClick={e => e.stopPropagation()}>
      <button type="button" onClick={toggle} className="text-xs text-primary hover:underline">
        {open ? '▾' : '▸'} {t('desk_floor_plan')}
      </button>
      {open && plans && !plans.length && (
        <div className="text-xs text-muted-foreground mt-1">{err || t('desk_floor_plan_none')}</div>
      )}
      {open && plan && (
        <div className="mt-1">
          {plans.length > 1 && (
            <div className="flex flex-wrap gap-1 mb-1">
              {plans.map((p, i) => (
                <button key={p.floor} type="button" onClick={() => setIdx(i)}
                  className={`text-[11px] px-1.5 rounded border ${i === idx ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>
                  {t('desk_floor_plan_floor', { n: p.floor })}
                </button>
              ))}
            </div>
          )}
          <svg viewBox={`0 0 ${sp.w} ${sp.h}`} className="w-full h-auto bg-white rounded border border-border"
            preserveAspectRatio="xMidYMid meet" role="img" aria-label={t('desk_floor_plan_floor', { n: plan.floor })}>
            <image href={plan.image_url} x="0" y="0" width={sp.w} height={sp.h} preserveAspectRatio="none" />
            {plan.flats.map(f => (
              <polygon key={f.unit_id} points={pts(f.polygon, sp.sx, sp.sy)} vectorEffect="non-scaling-stroke"
                fill={f.mine ? 'rgba(37,99,235,0.45)' : FREE.has(f.status) ? 'rgba(34,197,94,0.18)' : 'rgba(100,116,139,0.28)'}
                stroke={f.mine ? '#1d4ed8' : 'rgba(71,85,105,0.6)'} strokeWidth={f.mine ? 2 : 1}>
                <title>{`#${f.number} · ${f.status}`}</title>
              </polygon>
            ))}
          </svg>
          <div className="flex gap-3 text-[11px] text-muted-foreground mt-0.5">
            <span><span className="inline-block w-2.5 h-2.5 align-middle mr-1" style={{ background: 'rgba(37,99,235,0.6)' }} />{t('desk_floor_plan_mine')}</span>
            <span><span className="inline-block w-2.5 h-2.5 align-middle mr-1" style={{ background: 'rgba(34,197,94,0.4)' }} />{t('desk_floor_plan_free')}</span>
            <span><span className="inline-block w-2.5 h-2.5 align-middle mr-1" style={{ background: 'rgba(100,116,139,0.5)' }} />{t('desk_floor_plan_sold')}</span>
            {plan.source_url && <a href={plan.source_url} target="_blank" rel="noreferrer" className="ml-auto hover:underline">↗</a>}
          </div>
        </div>
      )}
    </div>
  )
}
